import type { OversightRatingByDocument } from '@risksmart-app/trpc/src/types';
import type { GetLatestOversightRatingsByDocumentIdsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLatestOversightRatingsByDocumentIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetLatestOversightRatingsByDocumentIdsArgs = {
  documentIds: string[];
};

/**
 * Reshapes the service-resolved "latest rating per document" rows back into
 * the GraphQL query response shape so the page hook's client-side resolution
 * (which both branches share) treats them uniformly. The service already
 * reduced each document to a single leaf, so one synthetic
 * `document_oversight_result` row per document is emitted; the shared resolver
 * then trivially selects it. `Id` and `TestDate` are unused by the resolver for
 * a single-row-per-parent input, so placeholders are safe.
 */
export const mapOversightRatingsByDocumentToGraphQL = (
  data: OversightRatingByDocument[]
): GetLatestOversightRatingsByDocumentIdsQuery => ({
  document_oversight_result: data.map((row) => ({
    Id: '',
    Rating: row.Rating,
    TestDate: null,
    CreatedAtTimestamp: row.CreatedAt ?? '',
    documentParent: [{ ParentId: row.DocumentId }],
  })),
});

export const useGetLatestOversightRatingsByDocumentIds = createQueryHook<
  UseGetLatestOversightRatingsByDocumentIdsArgs,
  OversightRatingByDocument[],
  GetLatestOversightRatingsByDocumentIdsQuery
>({
  trpcQueryOptions: (trpc, { documentIds }) =>
    trpc.frontend.oversight.ratingsByDocumentIds.queryOptions({
      documentIds,
    }),
  mapTrpcDataToGraphQL: mapOversightRatingsByDocumentToGraphQL,
  graphqlDocument: GetLatestOversightRatingsByDocumentIdsDocument,
  graphqlVariables: ({ documentIds }) => ({ DocumentIds: documentIds }),
  graphqlFetchPolicy: 'cache-and-network',
});
